import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  schemaJsonLd?: Record<string, unknown>;
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = 'website',
  schemaJsonLd,
}: SEOHeadProps) {
  useEffect(() => {
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = canonicalUrl;

    if (keywords) {
      let metaKw = document.querySelector('meta[name="keywords"]') as HTMLMetaElement | null;
      if (!metaKw) {
        metaKw = document.createElement('meta');
        metaKw.name = 'keywords';
        document.head.appendChild(metaKw);
      }
      metaKw.content = keywords;
    }

    const prevScript = document.querySelector('script[data-readdy-page-ld]');
    if (prevScript) prevScript.remove();

    if (schemaJsonLd) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-readdy-page-ld', 'true');
      script.textContent = JSON.stringify(schemaJsonLd);
      document.head.appendChild(script);
    }

    return () => {
      const script = document.querySelector('script[data-readdy-page-ld]');
      if (script) script.remove();
    };
  }, [canonicalUrl, keywords, schemaJsonLd]);

  const displayOgTitle = ogTitle || title;
  const displayOgDesc = ogDescription || description;

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta property="og:title" content={displayOgTitle} />
      <meta property="og:description" content={displayOgDesc} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={ogType} />
      <meta property="og:locale" content="fr_FR" />
      {ogImage && <meta property="og:image" content={ogImage} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={displayOgTitle} />
      <meta name="twitter:description" content={displayOgDesc} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}
    </>
  );
}